import { useMutation, useQueryClient } from '@tanstack/react-query';

import { importUpdateWorkflow } from '@/lib/api/generated/api';
import type {
  ImportRejection,
  RunningTask,
  WorkflowImportResult,
} from '@/lib/api/generated/model';
import type { AnswersRequest } from '../upload/answers';
import { invalidateWorkspace } from '@/features/workspaces';
import {
  WorkflowRejectedError,
  WorkflowUpdateBlockedError,
  WorkflowVersionConflictError,
} from '../errors';

/**
 * Replaces an existing workflow's definition in place from an uploaded YAML
 * (spec 201 FR-004). Modelled on `useImportWorkflow`, with one extra refusal:
 * a 409 whose body carries `running` means a task is still executing on the
 * workflow, and the whole update is blocked until it is stopped or done
 * (FR-005) — mapped to `WorkflowUpdateBlockedError` rather than the generic
 * rejection, so the dialog can show the running list without stale preflight
 * data.
 */
export function useImportUpdateWorkflow(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workflowId,
      file,
      answers,
    }: {
      workflowId: string;
      file: File;
      answers?: AnswersRequest;
    }): Promise<WorkflowImportResult> => {
      const response = await importUpdateWorkflow(workspaceId, workflowId, {
        file,
        ...(answers ? { substitutions: JSON.stringify(answers) } : {}),
      });
      if (response.status === 200) return response.data;
      if (response.status === 409) {
        // The committed openapi.json types every 409 here as `ImportRejection`
        // (violations only), but the service also sends a running-tasks body
        // and a version/key-mismatch body on this same status — a contract
        // gap tracked in backend-gaps.md, not a client mistake.
        const data = response.data as unknown as {
          running?: RunningTask[];
          error?: string;
          message?: string;
        };
        if (data.running) {
          throw new WorkflowUpdateBlockedError(data.running);
        }
        if (data.error === 'WORKFLOW_VERSION_CONFLICT') {
          throw new WorkflowVersionConflictError(data.message);
        }
        if (data.error === 'WORKFLOW_KEY_MISMATCH') {
          throw new Error(
            data.message ??
              "This file's key does not match the workflow being updated.",
          );
        }
        if (
          Array.isArray((response.data as { violations?: unknown }).violations)
        ) {
          throw new WorkflowRejectedError(
            response.status,
            response.data as ImportRejection,
          );
        }
        throw new Error(
          data.message ?? 'The workflow submission was rejected.',
        );
      }
      if (response.status === 404) {
        throw new Error('This workflow no longer exists.');
      }
      if (response.status === 413 || response.status === 422) {
        throw new WorkflowRejectedError(response.status, response.data);
      }
      throw new Error('The workflow could not be updated.');
    },
    onSuccess: () => invalidateWorkspace(queryClient, workspaceId),
  });
}
